import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Text,
  Image,
  Button,
  Group,
  FileButton,
  Stack,
  Alert,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconUpload,
  IconTrash,
  IconSignature,
  IconAlertCircle,
} from "@tabler/icons-react";
import { signatureService } from "../../../services/signature.service";

interface SignatureUploadProps {
  currentSignatureUrl?: string | null;
  hasExistingSignature?: boolean;
  onSignatureChange: (s3Key: string | null) => void;
}

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2 MB
const ALLOWED_TYPES = ["image/png", "image/jpeg"];

interface UploadedSignature {
  key: string;
  previewUrl: string;
}

const validateSignatureFile = (file: File): string | undefined => {
  if (!ALLOWED_TYPES.includes(file.type)) {
    return "Only PNG and JPEG files are allowed.";
  }
  if (file.size > MAX_FILE_SIZE) {
    return "File size must be less than 2 MB.";
  }
  return undefined;
};

const uploadSignatureFile = async (file: File): Promise<UploadedSignature> => {
  const { url, key } = await signatureService.getPresignedUrl(
    file.name,
    file.type
  );
  await signatureService.uploadToS3(url, file);
  return { key, previewUrl: URL.createObjectURL(file) };
};

const showSignatureUploadSuccess = () => {
  notifications.show({
    title: "Signature uploaded",
    message: "Signature image uploaded successfully.",
    color: "green",
  });
};

const showSignatureUploadFailure = () => {
  notifications.show({
    title: "Upload failed",
    message: "Could not upload signature. Please try again.",
    color: "red",
  });
};

export function SignatureUpload({
  currentSignatureUrl,
  hasExistingSignature = false,
  onSignatureChange,
}: SignatureUploadProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    currentSignatureUrl || null
  );
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resetRef = useRef<() => void>(null);
  const previewObjectUrlRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);

  const revokePreviewObjectUrl = useCallback(() => {
    if (!previewObjectUrlRef.current) return;
    URL.revokeObjectURL(previewObjectUrlRef.current);
    previewObjectUrlRef.current = null;
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      revokePreviewObjectUrl();
    };
  }, [revokePreviewObjectUrl]);

  const applyUploadedSignature = useCallback(
    ({ key, previewUrl: nextPreviewUrl }: UploadedSignature) => {
      revokePreviewObjectUrl();
      previewObjectUrlRef.current = nextPreviewUrl;
      setPreviewUrl(nextPreviewUrl);
      onSignatureChange(key);
    },
    [onSignatureChange, revokePreviewObjectUrl]
  );

  const completeSignatureUpload = useCallback(
    async (file: File) => {
      const uploadedSignature = await uploadSignatureFile(file);
      if (!isMountedRef.current) {
        URL.revokeObjectURL(uploadedSignature.previewUrl);
        return;
      }
      applyUploadedSignature(uploadedSignature);
      showSignatureUploadSuccess();
    },
    [applyUploadedSignature]
  );

  const finishUploadAttempt = useCallback(() => {
    if (!isMountedRef.current) return;
    setUploading(false);
    resetRef.current?.();
  }, []);

  const handleFileSelect = useCallback(
    async (file: File | null) => {
      if (!file) return;

      const validationError = validateSignatureFile(file);
      setError(validationError || null);
      if (validationError) return;

      setUploading(true);
      try {
        await completeSignatureUpload(file);
      } catch {
        if (isMountedRef.current) showSignatureUploadFailure();
      } finally {
        finishUploadAttempt();
      }
    },
    [completeSignatureUpload, finishUploadAttempt]
  );

  const handleRemove = useCallback(() => {
    revokePreviewObjectUrl();
    setPreviewUrl(null);
    setError(null);
    onSignatureChange(null);
  }, [onSignatureChange, revokePreviewObjectUrl]);

  const handlePreviewError = useCallback(() => {
    setPreviewUrl(null);
  }, []);

  const handleErrorClose = useCallback(() => {
    setError(null);
  }, []);

  const hasSignature = useMemo(
    () => Boolean(previewUrl) || hasExistingSignature,
    [hasExistingSignature, previewUrl]
  );

  return (
    <Box>
      <Group gap="xs" mb="xs">
        <IconSignature size={18} />
        <Text fw={600} size="sm">
          Signature Image
        </Text>
      </Group>

      {error && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          color="red"
          variant="light"
          mb="sm"
          withCloseButton
          onClose={handleErrorClose}
        >
          {error}
        </Alert>
      )}

      <Stack gap="sm">
        {previewUrl && (
          <Box
            style={{
              border: "1px solid var(--mantine-color-default-border)",
              borderRadius: 4,
              padding: 8,
              backgroundColor: "var(--mantine-color-body)",
              textAlign: "center",
            }}
          >
            <Image
              src={previewUrl}
              alt="Signature preview"
              fit="contain"
              style={{ maxHeight: 80, maxWidth: 250 }}
              onError={handlePreviewError}
            />
          </Box>
        )}

        {!previewUrl && hasExistingSignature && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="yellow"
            variant="light"
          >
            An existing signature is saved, but its preview could not be loaded.
            You can replace or remove it.
          </Alert>
        )}

        <Group gap="sm">
          <FileButton
            resetRef={resetRef}
            onChange={handleFileSelect}
            accept="image/png,image/jpeg"
          >
            {(props) => (
              <Button
                {...props}
                variant="light"
                leftSection={<IconUpload size={16} />}
                loading={uploading}
                size="xs"
              >
                {hasSignature ? "Replace Signature" : "Upload Signature"}
              </Button>
            )}
          </FileButton>

          {hasSignature && (
            <Button
              variant="subtle"
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={handleRemove}
              size="xs"
            >
              Remove
            </Button>
          )}
        </Group>

        <Text size="xs" c="dimmed">
          PNG or JPEG, max 2 MB
        </Text>
      </Stack>
    </Box>
  );
}
